import { Box, Text } from '@mantine/core';
import type { GroupSummary } from '@/generated/types/navigator/types.gen';
import { FuelsSection } from '@/views/map/popover/FuelsSection';
import { GoogleEvents } from '@/features/googleEvents';
import { useViewedOnceAnalytics } from '@/hooks/useViewedOnceAnalytics';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { RiskMetricsSection } from './RiskMetricsSection';
import { Section } from './Section';

interface GroupRiskSummaryProps {
  summary: GroupSummary;
}

/**
 * Wildfire-objective Summary tab content for a single network group.
 * Two sections: top-line risk metrics and fuel distribution breakdown.
 */
export const GroupRiskSummary = ({ summary }: GroupRiskSummaryProps) => {
  const { formatMessage } = useClientTerminology();
  const fuelsRef = useViewedOnceAnalytics<HTMLDivElement>(
    GoogleEvents.networkGroupFuelsViewed,
    summary.name
  );
  return (
    <Box px="md" pb="md" data-testid="group-risk-summary">
      <RiskMetricsSection
        wildfireRisk={summary.wildfireRisk}
        wildfireBurnability={summary.wildfireBurnability}
        wildfireExposure={summary.wildfireExposure}
      />
      <div ref={fuelsRef}>
        <Section
          name={formatMessage({ id: 'groupSummary.fuelDistribution.title' })}
          showDivider
        >
          {summary.fuelCategories ? (
            <div data-testid="group-risk-summary-fuels">
              <FuelsSection fuelCategories={summary.fuelCategories} />
            </div>
          ) : (
            <Text
              size="sm"
              c="dimmed"
              data-testid="group-risk-summary-fuels-empty"
            >
              {formatMessage({ id: 'groupSummary.fuelDistribution.empty' })}
            </Text>
          )}
        </Section>
      </div>
    </Box>
  );
};
